import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react"

const POPUP_SHOWN_KEY = "leanex_newsletter_popup_shown"
const SUBSCRIBED_KEY = "leanex_newsletter_subscribed"
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000 // 30 päivää
// Polut, joilla uutiskirjepopup EI saa avautua automaattisesti:
//  - /kotiturva: kampanjalanding, jolla on oma yksi CTA (popup ei saa kilpailla)
//  - /wayfin: tuotesivu samalla periaatteella (yksi CTA: rekisteröityminen
//    wayfin.eu:hun)
//  - /saatelywiki: pitkä tietosisältö, jota luetaan keskittyneesti — automaattinen
//    popup keskeytti lukemisen käytettävyystestissä (3/10 testaajaa). Kattaa myös
//    säädössivut /saatelywiki/:slug (saatelywiki/*-komponentit). Manuaalinen tilaus
//    muualla ja popupin avaus koodista (open()) toimivat yhä normaalisti.
//  - /tietosuoja: tietosuojaselosteen lukijalle ei ponnahduslomaketta kesken
//    lukemisen (ristiriita sivun oman sisällön kanssa).
//  - /kartoitus: maksettu asiakaskohtainen deliverable. Markkinointimodaali
//    maksetun työn päälle on pahin mahdollinen signaali — ja sama popup osuisi
//    myös työpajaan, jossa Teemu jakaa ruutua asiakkaalle.
//  - /kartoitus-studio: sisäinen työtila, ei markkinointipinta.
const AUTO_OPEN_EXCLUDED_PATHS = [
  "/kotiturva",
  "/wayfin",
  "/aintranet",
  "/saatelywiki",
  "/tietosuoja",
  "/kartoitus",
]

interface NewsletterContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
  markSubscribed: () => void
}

const NewsletterContext = createContext<NewsletterContextValue | null>(null)

function hasBeenShownRecently(): boolean {
  if (typeof window === "undefined") return false
  const ts = localStorage.getItem(POPUP_SHOWN_KEY)
  if (!ts) return false
  return Date.now() - parseInt(ts, 10) < COOLDOWN_MS
}

function hasSubscribed(): boolean {
  if (typeof window === "undefined") return false
  return !!localStorage.getItem(SUBSCRIBED_KEY)
}

export function NewsletterProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const autoTriggeredRef = useRef(false)

  useEffect(() => {
    const autoOpen = () => {
      if (AUTO_OPEN_EXCLUDED_PATHS.some(p => window.location.pathname.startsWith(p))) return
      if (autoTriggeredRef.current || hasBeenShownRecently() || hasSubscribed()) return
      autoTriggeredRef.current = true
      localStorage.setItem(POPUP_SHOWN_KEY, Date.now().toString())
      setIsOpen(true)
    }

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) autoOpen()
    }

    const handleScroll = () => {
      const denom = document.body.scrollHeight - window.innerHeight
      if (denom <= 0) return
      const scrolled = window.scrollY / denom
      if (scrolled >= 0.6) autoOpen()
    }

    document.addEventListener("mouseleave", handleMouseLeave)
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const value: NewsletterContextValue = {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    markSubscribed: () => {
      localStorage.setItem(SUBSCRIBED_KEY, "true")
      // Modal jätetään auki, jotta käyttäjä näkee onnistumistilan.
      // Sulkeminen tapahtuu vasta käyttäjän omasta close-toiminnosta.
    },
  }

  return <NewsletterContext.Provider value={value}>{children}</NewsletterContext.Provider>
}

export function useNewsletter(): NewsletterContextValue {
  const ctx = useContext(NewsletterContext)
  if (!ctx) {
    throw new Error("useNewsletter must be used within a NewsletterProvider")
  }
  return ctx
}
